/**
 * TextHandlers - Handles text messaging during voice calls
 * 
 * Required from parent (voiceInterface):
 * - sdk.voiceSDK: VoiceSDK instance
 * - isActive: boolean (call must be active)
 * - shadowRoot: ShadowRoot | Document (for desktop)
 * - isMobile: boolean
 * - t(key): string (translation helper)
 * - addConversationMessage(role, content): void
 * - addMobileMessage(type, text): void (when mobile)
 * 
 * State owned by this module:
 * - _textInjectTimeout: number | null - Desktop ACK timeout
 * - _mobileTextInjectTimeout: number | null - Mobile ACK timeout
 * - _statusClearTimeout: number | null - Desktop status clear timeout
 * - _mobileStatusClearTimeout: number | null - Mobile status clear timeout
 * - _originalHintText: string | null - Original desktop hint text
 * - _originalMobileHintText: string | null - Original mobile hint text
 */

export class TextHandlers {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    this.shadowRoot = voiceInterface.shadowRoot;
    this._textInjectTimeout = null;
    this._mobileTextInjectTimeout = null;
    this._statusClearTimeout = null;
    this._mobileStatusClearTimeout = null;
    this._originalHintText = null;
    this._originalMobileHintText = null;
    this._textInjectAckHandler = null; // Store handler reference here, not on parent
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Setup text inject acknowledgment handler
   */
  setupTextInjectAckHandler() {
    if (this.voiceInterface.sdk?.voiceSDK) {
      // Remove existing listener if any (to avoid duplicates)
      if (this._textInjectAckHandler) {
        this.voiceInterface.sdk.voiceSDK.off('textInjectAck', this._textInjectAckHandler);
      }
      // Create bound handler and store reference for later cleanup
      this._textInjectAckHandler = (data) => {
        this.handleTextInjectAck(data);
      };
      // Register listener using EventEmitter pattern
      this.voiceInterface.sdk.voiceSDK.on('textInjectAck', this._textInjectAckHandler);
    }
  }

  /**
   * Cleanup all timeouts and listeners
   */
  cleanup() {
    // Clear timeouts
    if (this._textInjectTimeout) {
      clearTimeout(this._textInjectTimeout);
      this._textInjectTimeout = null;
    }
    if (this._mobileTextInjectTimeout) {
      clearTimeout(this._mobileTextInjectTimeout);
      this._mobileTextInjectTimeout = null;
    }
    if (this._statusClearTimeout) {
      clearTimeout(this._statusClearTimeout);
      this._statusClearTimeout = null;
    }
    if (this._mobileStatusClearTimeout) {
      clearTimeout(this._mobileStatusClearTimeout);
      this._mobileStatusClearTimeout = null;
    }

    // Reset hint text references
    this._originalHintText = null;
    this._originalMobileHintText = null;
  }

  /**
   * Handle acknowledgment for text injection from backend
   */
  handleTextInjectAck(data) {
    // Clear desktop timeout
    if (this._textInjectTimeout) {
      clearTimeout(this._textInjectTimeout);
      this._textInjectTimeout = null;
    }

    // Clear mobile timeout
    if (this._mobileTextInjectTimeout) {
      clearTimeout(this._mobileTextInjectTimeout);
      this._mobileTextInjectTimeout = null;
    }

    // Re-enable desktop elements
    const desktopInput = this.shadowRoot.getElementById('voiceTextInput');
    const desktopSendBtn = this.shadowRoot.getElementById('voiceSendBtn');
    if (desktopInput) desktopInput.disabled = false;
    if (desktopSendBtn) desktopSendBtn.disabled = !desktopInput?.value?.trim();

    // Re-enable mobile elements
    const mobileInput = document.getElementById('mobileTextInput');
    const mobileSendBtn = document.getElementById('mobileSendBtn');
    if (mobileInput) mobileInput.disabled = false;
    if (mobileSendBtn) mobileSendBtn.disabled = !mobileInput?.value?.trim();

    // Show status on appropriate UI
    if (data.success) {
      const isQueued = data.message?.toLowerCase().includes('queued');
      const status = isQueued ? 'queued' : 'success';
      const message = isQueued ? 'Message queued' : 'Message sent';

      if (this.voiceInterface.isMobile) {
        this.showMobileTextInputStatus(status, message);
      } else {
        this.showDesktopTextInputStatus(status, message);
      }
    } else {
      const errorMsg = data.message || 'Failed to send';

      if (this.voiceInterface.isMobile) {
        this.showMobileTextInputStatus('error', errorMsg);
      } else {
        this.showDesktopTextInputStatus('error', errorMsg);
      }
    }
  }

  /**
   * Send text message through voice WebSocket connection (desktop)
   */
  sendDesktopTextMessage() {
    const input = this.shadowRoot.getElementById('voiceTextInput');
    const sendBtn = this.shadowRoot.getElementById('voiceSendBtn');
    
    if (!input || !sendBtn) {
      console.warn('VoiceInterface: Missing UI elements for desktop text send');
      return;
    }

    const text = input.value.trim();
    if (!text) {
      return;
    }

    this._sendTextMessage(text, input, sendBtn, false);
  }

  /**
   * Send text message through voice WebSocket connection (mobile)
   */
  sendMobileTextMessage() {
    const input = document.getElementById('mobileTextInput');
    const sendBtn = document.getElementById('mobileSendBtn');
    
    if (!input || !sendBtn) {
      console.warn('VoiceInterface: Missing UI elements for mobile text send');
      return;
    }

    this._sendTextMessage(input.value, input, sendBtn, true);
  }

  /**
   * Shared logic for sending text messages through voice WebSocket connection
   */
  _sendTextMessage(text, input, sendBtn, isMobile) {
    if (!this.voiceInterface.isActive || !this.voiceInterface.sdk?.voiceSDK) {
      console.warn('VoiceInterface: Cannot send text - call not active');
      return;
    }

    const trimmedText = text?.trim();
    if (!trimmedText) {
      this.showTextInputStatus(isMobile ? 'mobile' : 'desktop', 'error', 'Enter text first');
      return;
    }

    // Clear input and disable send button
    input.value = '';
    if (sendBtn) sendBtn.disabled = true;

    // Show sending status
    this.showTextInputStatus(isMobile ? 'mobile' : 'desktop', 'sending', 'Sending...');

    // Send via VoiceSDK
    try {
      const success = this.voiceInterface.sdk.voiceSDK.sendMessage({
        t: 'text_inject',
        text: trimmedText
      });

      if (success) {
        // Set timeout for ACK (will be cleared when ACK received)
        const timeout = setTimeout(() => {
          if (isMobile) {
            this._mobileTextInjectTimeout = null;
          } else {
            this._textInjectTimeout = null;
          }
          // Timeout - restore input
          input.disabled = false;
          if (sendBtn) sendBtn.disabled = false;
          this.showTextInputStatus(isMobile ? 'mobile' : 'desktop', 'error', 'No response');
        }, 5000);

        if (isMobile) {
          this._mobileTextInjectTimeout = timeout;
        } else {
          this._textInjectTimeout = timeout;
        }

        // Add user message to conversation
        if (isMobile) {
          this.voiceInterface.addMobileMessage('user', trimmedText);
        } else {
          this.voiceInterface.addConversationMessage('user', trimmedText);
        }
      } else {
        // Failed to send
        input.disabled = false;
        if (sendBtn) sendBtn.disabled = false;
        this.showTextInputStatus(isMobile ? 'mobile' : 'desktop', 'error', 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending text message:', error);
      input.disabled = false;
      if (sendBtn) sendBtn.disabled = false;
      this.showTextInputStatus(isMobile ? 'mobile' : 'desktop', 'error', 'Error sending');
    }
  }

  /**
   * Show status feedback for text input
   */
  showTextInputStatus(platform, status, message) {
    if (platform === 'mobile') {
      this.showMobileTextInputStatus(status, message);
    } else {
      this.showDesktopTextInputStatus(status, message);
    }
  }

  /**
   * Show status feedback for desktop text input
   */
  showDesktopTextInputStatus(status, message) {
    const hint = this.shadowRoot.querySelector('.voice-input-hint');
    if (!hint) return;

    // Store original text if not stored
    if (!this._originalHintText) {
      this._originalHintText = hint.textContent;
    }

    // Remove all status classes
    hint.classList.remove(
      'voice-input-hint--sending',
      'voice-input-hint--success',
      'voice-input-hint--queued',
      'voice-input-hint--error'
    );

    // Apply status class and message
    if (status === 'sending') {
      hint.classList.add('voice-input-hint--sending');
      hint.textContent = message || 'Sending...';
    } else if (status === 'success') {
      hint.classList.add('voice-input-hint--success');
      hint.textContent = message || 'Sent';
      this.clearTextInputStatusAfterDelay(3000);
    } else if (status === 'queued') {
      hint.classList.add('voice-input-hint--queued');
      hint.textContent = message || 'Queued';
    } else if (status === 'error') {
      hint.classList.add('voice-input-hint--error');
      hint.textContent = message || 'Error';
      this.clearTextInputStatusAfterDelay(3000);
    } else if (status === 'clear') {
      hint.textContent = this._originalHintText || this.t('voiceActive') || 'Voice is still active • Type or speak';
      this._originalHintText = null;
    }
  }

  /**
   * Show status feedback for mobile text input
   */
  showMobileTextInputStatus(status, message) {
    const hint = document.querySelector('.mobile-input-hint');
    if (!hint) return;

    // Store original text if not stored
    if (!this._originalMobileHintText) {
      this._originalMobileHintText = hint.textContent;
    }

    // Remove all status classes
    hint.classList.remove(
      'mobile-input-hint--sending',
      'mobile-input-hint--success',
      'mobile-input-hint--queued',
      'mobile-input-hint--error'
    );

    // Apply status class and message
    if (status === 'sending') {
      hint.classList.add('mobile-input-hint--sending');
      hint.textContent = message || 'Sending...';
    } else if (status === 'success') {
      hint.classList.add('mobile-input-hint--success');
      hint.textContent = message || 'Sent';
      this.clearMobileTextInputStatusAfterDelay(3000);
    } else if (status === 'queued') {
      hint.classList.add('mobile-input-hint--queued');
      hint.textContent = message || 'Queued';
    } else if (status === 'error') {
      hint.classList.add('mobile-input-hint--error');
      hint.textContent = message || 'Error';
      this.clearMobileTextInputStatusAfterDelay(3000);
    } else if (status === 'clear') {
      hint.textContent = this._originalMobileHintText || '🎙️ Voice is still active • Type or speak';
      this._originalMobileHintText = null;
    }
  }

  /**
   * Clear desktop status and restore original hint after delay
   */
  clearTextInputStatusAfterDelay(delay = 3000) {
    if (this._statusClearTimeout) {
      clearTimeout(this._statusClearTimeout);
    }
    this._statusClearTimeout = setTimeout(() => {
      this.showDesktopTextInputStatus('clear');
      this._statusClearTimeout = null;
    }, delay);
  }

  /**
   * Clear mobile status and restore original hint after delay
   */
  clearMobileTextInputStatusAfterDelay(delay = 3000) {
    if (this._mobileStatusClearTimeout) {
      clearTimeout(this._mobileStatusClearTimeout);
    }
    this._mobileStatusClearTimeout = setTimeout(() => {
      this.showMobileTextInputStatus('clear');
      this._mobileStatusClearTimeout = null;
    }, delay);
  }
}
